import { motion } from 'framer-motion';
import { Box } from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { Alert } from '../../../shared/lib/common/Alert';
import { CustomLoader } from '../../../shared/lib/common/CustomLoader';
import { PageHeading, withTrademark } from '../../../shared/lib/common/PageHeading';
import palette from '../../../shared/lib/theme/palette';
import { flexCenter } from '../../../shared/lib/theme/styleConstants';
import { BillingStep } from '../app/interfaceTypes';
import { PAuthActionType } from '../app/reducer/PAuthReducer';
import { Plan, useFetchPlanLazyQuery, useGetSubscriptionDetailLazyQuery } from '../generated';
import { PlanDetailsCreateSubscription } from './PlanDetailsCreateSubscription';
import { useAppContext } from '../app/contexts/AppContext';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PendingPromoAlert from './common/PendingPromoAlert';
import { PENDING_PROMO_CODE_INFO_PLAN_SELECTION } from '../app/constants';
import { ONBOARDING_SELECT_PLAN_STRINGS } from '../../../shared/lib/constants';
import { useMobileView } from '../app/hooks/useMobileView';

export const BillingStepOne: FC<BillingStep> = ({ setBillingCard, dispatch, state }) => {
  const [newPlans, setNewPlans] = useState<Array<Plan[]>>();
  const mediumViewport = useMobileView();
  const { blackOne } = palette;

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const { currentUser } = useAppContext();
  const { isSubscribed } = currentUser || {};
  const { customerStripeId } = isSubscribed || {};

  const [fetchPlans, { loading: planLoading }] = useFetchPlanLazyQuery({
    fetchPolicy: 'network-only',
    onError({ message }) {
      Alert.error(message);
    },
    onCompleted(data) {
      const {
        fetchPlan: { data: planData, trialEnded }
      } = data;

      dispatch({ type: PAuthActionType.SET_FREE_PLAN_USED, freePlanUsed: trialEnded ?? false });
      dispatch({ type: PAuthActionType.SET_PLANS, plans: planData });

      if (planData && planData.length > 0) {
        const groupBy = (objectArray: Plan[], property: string) => {
          return objectArray.reduce((acc: { [key: string]: Plan[] }, obj) => {
            const key = obj[property as keyof Plan] as string;
            if (!acc[key]) acc[key] = [];
            acc[key].push(obj);
            return acc;
          }, {});
        };

        const groupedPlan = groupBy(planData, 'product');
        const productNames = [...new Set(planData.map((p) => p.product))];
        // const updatedPlanResult = productNames.map((p) => groupedPlan[p]);
        const updatedPlanResult = productNames
          .map((p) => groupedPlan[p])
          .sort((a, b) => {
            const aHasZero = a.some((plan) => plan.amount === 0);
            const bHasZero = b.some((plan) => plan.amount === 0);
            return aHasZero === bHasZero ? 0 : aHasZero ? 1 : -1;
          });

        setNewPlans(updatedPlanResult);
      }
    }
  });

  const [subscriptionDetail, { loading: subLoading }] = useGetSubscriptionDetailLazyQuery({
    fetchPolicy: 'network-only',
    onError({ message }) {
      Alert.error(message);
    },
    onCompleted(data) {
      const { getSubscriptionDetail } = data || {};
      if (getSubscriptionDetail) {
        const { data: getSubscriptionDetailData } = getSubscriptionDetail || {};
        const { items } = getSubscriptionDetailData[0] || {};
        const { data: itemsData } = items || {};
        const { plan } = itemsData.slice(-1)[0] || {};
        dispatch({ type: PAuthActionType.SET_SUBSCRIBED_PLAN_ID, subscribedPlanId: plan?.id });
      }
    }
  });

  useEffect(() => {
    if (customerStripeId) {
      subscriptionDetail({ variables: { customerStripeId } });
    }
  }, [customerStripeId]);

  useEffect(() => {
    (async () => await fetchPlans())();
  }, []);

  const loading = subLoading || planLoading;

  // 🔹 Scroll handlers
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -scrollContainerRef.current.clientWidth * 0.9,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: scrollContainerRef.current.clientWidth * 0.9,
        behavior: 'smooth'
      });
    }
  };

  // 🔹 Update arrow visibility based on scroll position
  const updateScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollLeft: left, scrollWidth, clientWidth } = container;
    setCanScrollLeft(left > 10);
    setCanScrollRight(scrollWidth - left - clientWidth > 10);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Update when container size or scroll changes
    updateScrollButtons();

    container.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    // 👇 Observe when content inside container changes size
    const resizeObserver = new ResizeObserver(() => {
      updateScrollButtons();
    });
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
      resizeObserver.disconnect();
    };
  }, [newPlans]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" width="100%" padding={0}>
      <PendingPromoAlert message={PENDING_PROMO_CODE_INFO_PLAN_SELECTION} />

      <Box mt={mediumViewport ? 0 : 4} mb={mediumViewport ? 2 : 4} sx={flexCenter} flexDirection="column" textAlign="center">
        <PageHeading color={blackOne} variant={mediumViewport ? 'h5_mobile' : 'h3'} title={withTrademark(ONBOARDING_SELECT_PLAN_STRINGS.TITLE)} subTitle={ONBOARDING_SELECT_PLAN_STRINGS.DESCRIPTION} />
      </Box>

      <Box width="100%" maxWidth="100%" position="relative">
        {/* 🔹 Animated left arrow */}
        {mediumViewport && canScrollLeft && (
          <motion.div
            onClick={scrollLeft}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            style={{
              position: 'absolute',
              top: '-34px',
              left: '15px',
              zIndex: 2
            }}
          >
            <motion.div animate={{ x: [0, -6, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}>
              <ArrowBackIosNewIcon sx={{ color: blackOne, fontSize: 20 }} />
            </motion.div>
          </motion.div>
        )}

        {/* 🔹 Animated right arrow + text */}
        {mediumViewport && canScrollRight && (
          <motion.div
            onClick={scrollRight}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            style={{
              position: 'absolute',
              top: '-34px',
              right: '10px',
              zIndex: 2,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            whileTap={{ scale: 0.9 }} // optional click bump
          >
            <motion.span
              animate={{ opacity: [1, 0.6, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                fontSize: '12px',
                color: blackOne,
                fontWeight: 700,
                whiteSpace: 'nowrap'
              }}
            >
              Swipe to see more options
            </motion.span>

            <motion.div animate={{ x: [0, 6, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}>
              <ArrowForwardIosIcon sx={{ color: blackOne, fontSize: 20 }} />
            </motion.div>
          </motion.div>
        )}

        {/* 🔹 Scrollable area */}
        <Box
          ref={scrollContainerRef}
          display="flex"
          flexWrap="nowrap"
          overflow="auto"
          alignItems="stretch"
          gap={mediumViewport ? 0 : '10px'}
          justifyContent={mediumViewport ? 'flex-start' : 'center'}
          sx={{
            padding: mediumViewport ? '10px' : 0,
            paddingBottom: '30px',
            scrollBehavior: 'smooth',
            scrollSnapType: 'x mandatory',
            '& > *': { scrollSnapAlign: 'start' },
            '&::-webkit-scrollbar': { display: 'none' }
          }}
        >
          {loading ? (
            <CustomLoader height="calc(100vh - 500px)" />
          ) : (
            newPlans?.map((product, index) => <PlanDetailsCreateSubscription key={index} product={product} setBillingCard={setBillingCard} dispatch={dispatch} state={state} />)
          )}
        </Box>
      </Box>
    </Box>
  );
};
